import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';

import { useApi } from 'libs/data-access/src/api/useApi';

import useApp from '../../Hooks/useApp';
import { ILoginParams } from '../../../types';
import { AppContext } from '../../../AppContext';
import { ALL_ALLOWED_ROUTES } from '../../../App';
import { useAuth } from '../../../libs/data-access';
import { showToast } from './../../../utils/utility';
import { handleValidateLoginParams } from '../../../utils/validators';

export default function useAuthentication() {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Read query param
    const affiliateRegistration = searchParams.get('affiliateRegistration');
    const data = searchParams.get("data");
    const extraDetails = searchParams.get("extraDetails");


    const {
        setRole,
        setRefreshToken,
        setToken,
        setIsLoggedIn,
        handleUser,
        setUserAuthorities,
        setActiveRoute
    } = useContext(AppContext);

    const {
        handleLogin,
    } = useAuth();
    const {
        postData,
    } = useApi();
    const {
        handleContextStateUpdateOnMount
    } = useApp();

    const [showOnboardAgentModal, {
        open: openOnboardAgentModal,
        close: closeOnboardAgentModal
    }] = useDisclosure();

    const [loginParams, setLoginParams] = useState<ILoginParams>({
        userName: "",
        password: "",
        deviceToken: "",
        twoFactorEnabled: true
    });
    const [otp, setOTP] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"admin" | "agent">("admin");
    const [activeStep, setActiveStep] = useState<number>(1);
    const [isManagingOTP, setIsManagingOTP] = useState<boolean>(false);
    const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);


    // detect when query param changes
    useEffect(() => {
        if (affiliateRegistration?.toLowerCase() === "true") {
            openOnboardAgentModal();
        };
    }, [affiliateRegistration]);

    useEffect(() => {
        if (!data || !extraDetails) return;

        manageLoginStates({
            data: JSON.parse(data),
            extraDetails: JSON.parse(extraDetails)
        });
    }, [data]);


    /* HANDLER FUNCTIONS */
    const setQueryParam = (key: string, value: string) => {
        searchParams.set(key, value);
        setSearchParams(searchParams); // triggers rerender with new query
    };

    const removeQueryParam = (key: string) => {
        searchParams.delete(key);
        setSearchParams(searchParams); // triggers URL update
    };

    const handleLoginPayloadChange = (
        key: keyof ILoginParams,
        value: string
    ) => {
        setLoginParams(prev => ({ ...prev, [key]: value }));
    };

    const handleCancelOTPVerification = () => {
        const currentUrl = window.location.href;
        const url = new URL(currentUrl);

        url.searchParams.delete("verify");

        // Update the URL without reloading the page
        window.history.replaceState(null, "", url.toString());
        // window.history.pushState({}, "", url); // Update the URL in the browser
        setActiveStep(1);
    };

    const manageLoginStates = (response: any) => {
        const resData = response?.data;
        const user = response?.data?.user;
        const othersDetails = response?.extraDetails;
        const userAuthorities = response?.extraDetails?.authorities;

        handleUser(user);
        setUserAuthorities(userAuthorities);
        setRole(othersDetails.role);
        setToken(resData?.accessToken);
        setRefreshToken(resData?.refreshToken);
        setIsLoggedIn(true);
        setActiveRoute({ route: ALL_ALLOWED_ROUTES[0], SubMenuIndex: [] });

        localStorage.setItem("userAuthorities", JSON.stringify(userAuthorities));
        localStorage.setItem("role", JSON.stringify(othersDetails.role));
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("refreshToken", resData?.refreshToken);
        localStorage.setItem("token", resData?.accessToken);
        localStorage.setItem("isLoggedIn", JSON.stringify(true));
        localStorage.setItem("activeRoute", JSON.stringify({ route: ALL_ALLOWED_ROUTES[0], SubMenuIndex: [] }));
        // filterRoutesByRoles(ALL_ROUTES)
        window.location.reload();
    };


    /* API CALLS */
    const sendOTP = async () => {
        if (loginParams.userName.trim().length < 1) {
            showToast({
                type: "warning",
                message: "Please provide username"
            });
            return;
        };

        setIsManagingOTP(true);

        const userName = loginParams.userName.trim()

        const response = await postData(`unsecure/users/login/send-otp?userName=${userName}`, null);

        if (response.isSuccess) {
            // console.log("Response while sending OTP: ", response.data.data);

            // alert("Your OTP is: " + response.data.data);

            const currentUrl = window.location.href;
            const url = new URL(currentUrl);

            url.searchParams.set("verify", "otp");
            window.history.pushState({}, "", url); // Update the URL in the browser

            setActiveStep(2);
        } else {
            console.log("Error while sending OTP: ", response.error);
            showToast({
                type: "error",
                title: "Error while sending OTP",
                message: response?.message || "Error while sending OTP"
            });
        };

        setIsManagingOTP(false);
    };

    const verifyOTP = async () => {
        if (!otp) {
            showToast({
                type: "warning",
                message: "Please enter OTP to verify"
            });
            return;
        };

        if (otp.length < 6) {
            showToast({
                type: "warning",
                message: "OTP must be 6 digits long"
            });
            return;
        };

        if (loginParams.userName.trim().length < 1) {
            showToast({
                type: "warning",
                message: "Please enter username"
            });
            return;
        };

        setIsManagingOTP(true);

        const payload = {
            email: loginParams.userName.trim(),
            otp: otp
        };

        const response = await postData(`unsecure/users/login/verify-otp`, payload);

        if (response.isSuccess) {
            console.log("Response while fetching OTP: ", response.data.data);
            const isVerified = response.data.data;

            if (isVerified) {
                handleUserLogin();
            };
        } else {
            console.log("Error while sending OTP: ", response.error);
            showToast({
                type: "error",
                title: "Error while sending OTP",
                message: response?.message || "Error while sending OTP"
            });
        };

        setIsManagingOTP(false);
    };

    const handleUserLogin = async () => {

        const isValidated = handleValidateLoginParams(loginParams);

        if (!isValidated) return;

        setIsLoggingIn(true);

        const payload: ILoginParams = {
            userName: loginParams.userName.trim(),
            password: loginParams.password.split("/").join(""),
            deviceToken: "gdgdgd",
            twoFactorEnabled: true
        };

        const response = await handleLogin(`${activeTab === "admin" ? "admin" : "affiliate"}`, payload);

        setIsLoggingIn(false);

        if (response.isSuccess) {

            // console.log("Response for login: ", response);
            // console.log("Extra data: ", response.data?.extraDetails);

            manageLoginStates(response.data);

        } else {
            console.error("Error while login: ", response.error?.response?.status);
            showToast({
                type: "error",
                title: response.message || "Something Went Wrong!",
                message: `Could not process login ${response.error?.response?.status ?? ""}`
            });
        };

    };


    return {
        navigate,
        activeStep,
        setActiveStep,

        activeTab,
        setActiveTab,
        loginParams,
        isLoggingIn,
        handleLoginPayloadChange,
        handleUserLogin,

        otp,
        setOTP,
        isManagingOTP,
        handleCancelOTPVerification,
        sendOTP,
        verifyOTP,

        showOnboardAgentModal,
        openOnboardAgentModal,
        closeOnboardAgentModal,
        removeQueryParam
    };
}